/**
 * Cognito auth for the storefront: same User Pool as admin, without admin-group checks.
 * Configure REACT_APP_USER_POOL_ID and REACT_APP_USER_POOL_CLIENT_ID (and optional REACT_APP_COGNITO_DOMAIN).
 */

function getConfig() {
  const userPoolId = process.env.REACT_APP_USER_POOL_ID;
  const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
  const regionEnv = process.env.REACT_APP_COGNITO_REGION;
  const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
  const region =
    (typeof userPoolId === 'string' && userPoolId.includes('_') ? userPoolId.split('_')[0]! : undefined) ||
    regionEnv;
  return { userPoolId, clientId, region, cognitoDomain };
}

export function isCognitoCustomerConfigured(): boolean {
  const { userPoolId, clientId } = getConfig();
  return Boolean(userPoolId && clientId);
}

/**
 * Checks whether localStorage contains any active Cognito session keys.
 * If false, the user is an unauthenticated guest, so we must avoid calling Amplify
 * fetchAuthSession() which sends invalid token requests resulting in 400 Bad Request to Cognito IDP.
 */
export function hasCognitoSessionInStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const { clientId } = getConfig();
    if (clientId) {
      const lastUser = window.localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
      if (lastUser && lastUser.trim()) return true;
    }
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.LastAuthUser')) {
        const val = window.localStorage.getItem(key);
        if (val && val.trim()) return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

let configPromise: Promise<void> | null = null;

export function ensureCognitoCustomerConfigured(): Promise<void> {
  if (!isCognitoCustomerConfigured()) return Promise.resolve();
  if (configPromise) return configPromise;
  const { userPoolId, clientId, cognitoDomain } = getConfig();
  const poolId = typeof userPoolId === 'string' ? userPoolId : '';
  const appClientId = typeof clientId === 'string' ? clientId : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const callbackUrl = origin ? `${origin}/auth/callback` : '';
  const logoutUrl = origin || 'https://www.stylewithvikashini.com';
  configPromise = import('aws-amplify').then(({ Amplify }) => {
    const loginWith: {
      email: boolean;
      oauth?: {
        domain: string;
        redirectSignIn: string[];
        redirectSignOut: string[];
        responseType: 'code';
        scopes: string[];
      };
    } = {
      email: true,
      ...(cognitoDomain && typeof cognitoDomain === 'string' && callbackUrl
        ? {
            oauth: {
              domain: cognitoDomain,
              redirectSignIn: [callbackUrl],
              redirectSignOut: [logoutUrl],
              responseType: 'code' as const,
              scopes: ['email', 'openid', 'profile'],
            },
          }
        : {}),
    };
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: poolId,
          userPoolClientId: appClientId,
          loginWith,
        },
      },
    });
  });
  return configPromise;
}

export type CustomerSignInResult = { idToken: string } | { requiresNewPassword: true };

export async function signInCustomerCognito(username: string, password: string): Promise<CustomerSignInResult> {
  await ensureCognitoCustomerConfigured();
  const { signIn, fetchAuthSession } = await import('aws-amplify/auth');
  const usernameTrimmed = username.trim();
  try {
    const result = await signIn({ username: usernameTrimmed, password });
    const nextStep = (result as { nextStep?: { signInStep?: string } }).nextStep;
    if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { requiresNewPassword: true };
    }
  } catch (err: unknown) {
    const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : '';
    if (msg.includes('NotAuthorizedException') || msg.includes('User does not exist')) {
      throw new Error('Invalid email or password.');
    }
    if (msg.includes('UserNotFoundException')) {
      throw new Error('No account found with this email.');
    }
    if (msg.includes('NetworkError') || msg.includes('fetch')) {
      throw new Error('Network error. Check your connection and Cognito region settings.');
    }
    throw err instanceof Error ? err : new Error('Sign in failed.');
  }

  let raw: string | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const session = await fetchAuthSession({ forceRefresh: attempt > 0 });
    const idToken = session.tokens?.idToken;
    if (idToken) {
      raw = typeof idToken.toString === 'function' ? idToken.toString() : String(idToken);
      break;
    }
    if (attempt < 2) await new Promise((r) => setTimeout(r, 300));
  }
  if (!raw) throw new Error('No session after sign in');
  return { idToken: raw };
}

export async function confirmCustomerNewPassword(newPassword: string): Promise<string> {
  await ensureCognitoCustomerConfigured();
  const { confirmSignIn, fetchAuthSession } = await import('aws-amplify/auth');
  await confirmSignIn({ challengeResponse: newPassword });
  let raw: string | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const session = await fetchAuthSession({ forceRefresh: attempt > 0 });
    const idToken = session.tokens?.idToken;
    if (idToken) {
      raw = typeof idToken.toString === 'function' ? idToken.toString() : String(idToken);
      break;
    }
    if (attempt < 2) await new Promise((r) => setTimeout(r, 300));
  }
  if (!raw) throw new Error('No session after password change');
  return raw;
}

export async function signUpCustomerCognito(email: string, password: string, name: string): Promise<void> {
  await ensureCognitoCustomerConfigured();
  const { signUp } = await import('aws-amplify/auth');
  await signUp({
    username: email.trim(),
    password,
    options: {
      userAttributes: {
        email: email.trim(),
        name: name.trim(),
      },
    },
  });
}

export async function signOutCustomerCognito(): Promise<void> {
  if (!isCognitoCustomerConfigured()) return;
  await ensureCognitoCustomerConfigured();
  const { signOut } = await import('aws-amplify/auth');
  try {
    await signOut();
  } catch {
    // ignore
  }
}

export async function redirectToCustomerHostedUi(): Promise<void> {
  if (!isCognitoCustomerConfigured()) return;
  const { cognitoDomain } = getConfig();
  if (!cognitoDomain || typeof cognitoDomain !== 'string') {
    throw new Error('REACT_APP_COGNITO_DOMAIN is required for Hosted UI login');
  }
  await ensureCognitoCustomerConfigured();
  const { signInWithRedirect } = await import('aws-amplify/auth');
  await signInWithRedirect();
}

export function customerHostedUiEnabled(): boolean {
  const { cognitoDomain } = getConfig();
  return isCognitoCustomerConfigured() && Boolean(cognitoDomain && typeof cognitoDomain === 'string');
}

export function getCustomerCognitoHostedUiLogoutUrl(): string {
  const { clientId, cognitoDomain } = getConfig();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const logoutUri = origin || 'https://www.stylewithvikashini.com';
  if (!cognitoDomain || !clientId) return logoutUri;
  return `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
}

export async function getCognitoCustomerIdToken(): Promise<string | null> {
  if (!isCognitoCustomerConfigured() || !hasCognitoSessionInStorage()) return null;
  await ensureCognitoCustomerConfigured();
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) return null;
    return typeof idToken.toString === 'function' ? idToken.toString() : String(idToken);
  } catch {
    return null;
  }
}

/**
 * ID token for authenticated API calls (profile, addresses). Matches sign-in: retries with
 * `forceRefresh` so we still obtain a token when `vastra_auth` lost `token` (JSON omits `undefined`)
 * but Amplify still has a Cognito session, or when the cached ID token needs refresh.
 */
export async function getCustomerIdTokenForApi(): Promise<string | null> {
  if (!isCognitoCustomerConfigured() || !hasCognitoSessionInStorage()) return null;
  await ensureCognitoCustomerConfigured();
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    let raw: string | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      const session = await fetchAuthSession({ forceRefresh: attempt > 0 });
      const idToken = session.tokens?.idToken;
      if (idToken) {
        raw = typeof idToken.toString === 'function' ? idToken.toString() : String(idToken);
        break;
      }
      if (attempt < 2) await new Promise((r) => setTimeout(r, 300));
    }
    const s = raw?.trim();
    return s || null;
  } catch {
    return null;
  }
}

/**
 * Bearer token for storefront API calls (profile, addresses, orders, checkout as logged-in user).
 * Prefer a live Cognito session over `auth.token`: persisted auth often drops `token` while Amplify still has a session.
 */
export async function resolveCustomerApiToken(storedToken: string | undefined | null): Promise<string | null> {
  if (isCognitoCustomerConfigured()) {
    const fromCognito = await getCustomerIdTokenForApi();
    if (fromCognito) return fromCognito;
  }
  const s = typeof storedToken === 'string' ? storedToken.trim() : '';
  return s || null;
}
